import { useContext, useMemo } from 'react'
import YmalOfferTileSmall from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Small/YmalOfferTileSmall'
import { getTileViewProps } from 'components/OfferList/OfferListTiles/YouMayAlsoLikeTile/Common/getTileViewProps'
import TopPicksForMeOfferSkeleton from 'components/Recommendations/TopPicksForMe/TopPicksForMeOfferSkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import CurrencyContext from 'contexts/currencyContext'
import { calcDistanceBetweenOffers } from 'lib/offer/offerUtils'
import type { DistanceUnit } from 'lib/geo/distanceUnits'
import { getAllOffers } from 'selectors/offerSelectors'
import { isCalendarYmalOffer } from './calendarYmalOffer'

interface Props {
  offerId: string
  position: number
  linkQuery: string
  duration?: number
  roomCount?: number
  checkIn?: string
  checkOut?: string
  seedOffer?: App.Offer | App.BedbankOffer
  distanceUnit: DistanceUnit
  defaultDepositAmountPercentage: number
  hasExactCheckIn: boolean
  alternativePrice?: number
  alternativeMemberPrice?: number
  alternativeValue?: number
}

function CalendarSimilarOfferTile({
  offerId,
  position,
  linkQuery,
  duration,
  roomCount,
  checkIn,
  checkOut,
  seedOffer,
  distanceUnit,
  defaultDepositAmountPercentage,
  hasExactCheckIn,
  alternativePrice,
  alternativeMemberPrice,
  alternativeValue,
}: Props) {
  const currency = useContext(CurrencyContext)
  const offer = useAppSelector(state => getAllOffers(state)[offerId])

  const viewProperties = useMemo(() => {
    if (!offer || !isCalendarYmalOffer(offer)) {
      return undefined
    }

    return getTileViewProps(offer, {
      currency,
      distance: seedOffer ? calcDistanceBetweenOffers(offer, seedOffer) : undefined,
      distanceUnit,
      distanceFrom: seedOffer?.property?.name,
      duration,
      defaultDepositAmountPercentage,
    })
  }, [currency, defaultDepositAmountPercentage, distanceUnit, duration, offer, seedOffer])

  if (!offer || !isCalendarYmalOffer(offer)) {
    return <TopPicksForMeOfferSkeleton />
  }

  return (
    <YmalOfferTileSmall
      anchorTarget="_blank"
      offer={offer}
      linkQuery={linkQuery}
      duration={viewProperties?.duration ?? duration}
      roomCount={hasExactCheckIn ? roomCount : 1}
      depositAmount={viewProperties?.depositAmount}
      depositAmountPercentage={viewProperties?.depositAmountPercentage}
      depositEnabled={viewProperties?.depositEnabled ?? false}
      depositThresholds={viewProperties?.depositThresholds}
      tourDepositType={viewProperties?.tourDepositType}
      distance={viewProperties?.distance}
      distanceFrom={viewProperties?.distanceFrom}
      distanceUnit={viewProperties?.distanceUnit ?? distanceUnit}
      position={position}
      data-testid="lere-calendar-tile-small"
      alternativeCheckIn={hasExactCheckIn ? checkIn : undefined}
      alternativeCheckOut={hasExactCheckIn ? checkOut : undefined}
      alternativePrice={hasExactCheckIn ? alternativePrice : undefined}
      alternativeMemberPrice={hasExactCheckIn ? alternativeMemberPrice : undefined}
      alternativeValue={hasExactCheckIn ? alternativeValue : undefined}
    />
  )
}

export default CalendarSimilarOfferTile
